/**
 * The microphone affordance — the app's signature control.
 *
 * Two accessibility requirements shape the API (docs/03-design-system.md §9):
 *   - hold-to-speak is a dexterity burden, so `mode: 'toggle'` is a first-class
 *     alternative, not a hidden setting
 *   - every state is announced, and a permission problem produces a specific,
 *     actionable message rather than a dead button
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type MicState =
  | 'idle'
  | 'listening'
  | 'processing'
  | 'success'
  | 'lowConfidence'
  | 'disabled'
  | 'noPermission';

export interface MicButtonProps {
  readonly state: MicState;
  /** `hold` matches the platform default; `toggle` is the accessibility alternative. */
  readonly mode?: 'hold' | 'toggle' | undefined;
  readonly size?: 'md' | 'lg' | undefined;
  readonly onStart: () => void;
  readonly onStop: () => void;
  readonly onRequestPermission?: (() => void) | undefined;
  /** 0–1 input level, drives the waveform. Ignored under reduced motion. */
  readonly level?: number | undefined;
  readonly label?: string | undefined;
}

const STATE_LABELS: Record<MicState, string> = {
  idle: 'Hold to speak',
  listening: 'Listening — release when you finish',
  processing: 'Checking that…',
  success: 'Got it',
  lowConfidence: "Couldn't hear that clearly",
  disabled: 'Not available right now',
  noPermission: 'Microphone access needed',
};

export function MicButton({
  state,
  mode = 'hold',
  size = 'lg',
  onStart,
  onStop,
  onRequestPermission,
  level = 0,
  label,
}: MicButtonProps): JSX.Element {
  const [active, setActive] = useState(false);
  const activeRef = useRef(false);

  const begin = useCallback(() => {
    if (state === 'disabled' || state === 'processing') return;
    if (state === 'noPermission') {
      onRequestPermission?.();
      return;
    }
    activeRef.current = true;
    setActive(true);
    onStart();
  }, [state, onStart, onRequestPermission]);

  const end = useCallback(() => {
    if (!activeRef.current) return;
    activeRef.current = false;
    setActive(false);
    onStop();
  }, [onStop]);

  // A pointer released outside the button, or a window blur mid-hold, must still end the
  // recording. Without this the learner is left in a stuck "listening" state.
  useEffect(() => {
    if (mode !== 'hold') return;
    const handleUp = () => end();
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('blur', handleUp);
    return () => {
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('blur', handleUp);
    };
  }, [mode, end]);

  const toggle = useCallback(() => {
    if (activeRef.current) end();
    else begin();
  }, [begin, end]);

  const isRecording = state === 'listening' || active;
  const effectiveLabel = label ?? STATE_LABELS[state];

  const holdHandlers =
    mode === 'hold'
      ? {
          onPointerDown: begin,
          onPointerUp: end,
          onPointerCancel: end,
          // Space and Enter must work for keyboard users; they act as a toggle even in
          // hold mode, because holding a key is not a reasonable requirement.
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault();
              if (!activeRef.current) begin();
            }
          },
          onKeyUp: (event: React.KeyboardEvent) => {
            if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault();
              end();
            }
          },
        }
      : { onClick: toggle };

  return (
    <div className="vl-mic">
      <button
        type="button"
        className={`vl-mic__button vl-mic__button--${size}`}
        data-state={state}
        data-recording={isRecording ? 'true' : 'false'}
        aria-pressed={mode === 'toggle' ? isRecording : undefined}
        aria-label={effectiveLabel}
        disabled={state === 'disabled'}
        {...holdHandlers}
      >
        <span aria-hidden="true" className="vl-mic__glyph">
          🎤
        </span>
        {isRecording && <Waveform level={level} />}
      </button>

      <p className="vl-mic__hint" aria-live="polite">
        {state === 'noPermission' ? (
          <>
            {/* Specific and actionable — never a silent dead mic. */}
            Microphone access is off. You can still use text mode.{' '}
            {onRequestPermission !== undefined && (
              <button type="button" className="vl-link" onClick={onRequestPermission}>
                Enable microphone
              </button>
            )}
          </>
        ) : mode === 'toggle' && state === 'idle' ? (
          'Tap to speak'
        ) : (
          effectiveLabel
        )}
      </p>
    </div>
  );
}

export interface WaveformProps {
  readonly level: number;
  readonly bars?: number | undefined;
}

/**
 * Amplitude-driven, never strobing (nothing in the product flashes above 3 Hz). Under
 * reduced motion the CSS collapses this to a single static level bar.
 */
export function Waveform({ level, bars = 5 }: WaveformProps): JSX.Element {
  const clamped = Math.max(0, Math.min(1, level));
  return (
    <span className="vl-waveform" aria-hidden="true" data-level={clamped.toFixed(2)}>
      {Array.from({ length: bars }, (_, i) => {
        // A centre-weighted profile reads as speech rather than as a loading spinner.
        const weight = 1 - Math.abs(i - (bars - 1) / 2) / bars;
        const height = 20 + clamped * 80 * weight;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length decorative bars; the index is the identity of a bar position.
          <span key={i} className="vl-waveform__bar" style={{ height: `${height}%` }} />
        );
      })}
    </span>
  );
}

export interface TargetSentenceProps {
  readonly text: string;
  readonly respelling?: string | null | undefined;
  readonly ipa?: string | null | undefined;
  readonly lang: string;
  readonly onPlay?: (() => void) | undefined;
  readonly onPlaySlow?: (() => void) | undefined;
}

/** The sentence the learner must say: large, with phonetics, at arm's-length size. */
export function TargetSentence({
  text,
  respelling,
  ipa,
  lang,
  onPlay,
  onPlaySlow,
}: TargetSentenceProps): JSX.Element {
  return (
    <div className="vl-target">
      <p className="vl-target__text" lang={lang}>
        {text}
      </p>
      {respelling != null && respelling.length > 0 && (
        <p className="vl-target__respelling">
          <span className="vl-visually-hidden">Pronounced </span>
          {respelling}
        </p>
      )}
      {ipa != null && ipa.length > 0 && (
        <p className="vl-target__ipa">
          <code className="vl-ipa">{ipa}</code>
        </p>
      )}
      <div className="vl-target__actions">
        {onPlay !== undefined && (
          <button
            type="button"
            className="vl-button vl-button--ghost vl-button--sm"
            onClick={onPlay}
          >
            ▶ Hear it
          </button>
        )}
        {onPlaySlow !== undefined && (
          <button
            type="button"
            className="vl-button vl-button--ghost vl-button--sm"
            onClick={onPlaySlow}
            aria-label="Hear it slowly"
          >
            🐌 Slowly
          </button>
        )}
      </div>
    </div>
  );
}
